// Тонкая обёртка над window.Telegram.WebApp (ADR-0001, ADR-0025). Здесь только типы и доступ к объекту:
// кнопками владеет BottomBar (ux/12 §5.1), отступами — viewport.ts, вибрацией — haptics.ts.
// Методы новее Bot API 7.0 вызываются только после isVersionAtLeast (ux/12 §5.4).

export interface Insets { top: number; bottom: number; left: number; right: number }

export interface TgButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

export interface TgMainButton extends TgButton {
  text: string;
  isActive: boolean;
  setParams(p: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
}

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

export interface TgWebApp {
  initData: string;
  platform: string;
  version: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  isFullscreen?: boolean;
  viewportHeight?: number;
  viewportStableHeight?: number;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  initDataUnsafe?: { start_param?: string };
  ready(): void;
  expand(): void;
  close?(): void;
  isVersionAtLeast?(v: string): boolean;
  onEvent?(name: string, cb: (...a: unknown[]) => void): void;
  offEvent?(name: string, cb: (...a: unknown[]) => void): void;
  BackButton?: TgButton;
  MainButton?: TgMainButton;
  SettingsButton?: TgButton;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  lockOrientation?(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(c: string): void;
  setBackgroundColor?(c: string): void;
  setBottomBarColor?(c: string): void;
  openInvoice?(url: string, cb?: (status: InvoiceStatus) => void): void;
  openLink?(url: string): void;
  requestWriteAccess?(cb?: (granted: boolean) => void): void;
  openTelegramLink?(url: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

/** Объект Telegram, если игра открыта внутри Telegram (initData не пуст). */
export function getWebApp(): TgWebApp | null {
  const w = window.Telegram?.WebApp;
  return w && w.initData ? w : null;
}

/**
 * Дождаться telegram-web-app.js (грузится async): внутри Telegram без него WebApp нет до конца сессии — ни кнопок,
 * ни оплаты. Не дольше `ms`; не дождались — null (вход идёт по initData из адреса).
 */
export function waitWebApp(ms = 3000, step = 50): Promise<TgWebApp | null> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      const wa = getWebApp();
      if (wa || Date.now() - t0 >= ms) { resolve(wa); return; }
      setTimeout(tick, step);
    };
    tick();
  });
}

/**
 * Версию прислал сам Telegram (tgWebAppVersion в адресе). Без неё telegram-web-app.js ставит 6.0 — это «неизвестно»,
 * а не «старый клиент»: такой запуск не отправляем на SCR-UNSUPPORTED (живой Telegram Web, кнопка «Open»).
 */
export function versionKnown(): boolean {
  const wv = (window.Telegram as { WebView?: { initParams?: Record<string, string> } } | undefined)?.WebView;
  return !wv?.initParams || !!wv.initParams['tgWebAppVersion'];
}

/** Сравнение версий Bot API, если у клиента нет isVersionAtLeast (очень старые клиенты). */
export function versionAtLeast(wa: Pick<TgWebApp, 'version' | 'isVersionAtLeast'>, v: string): boolean {
  if (wa.isVersionAtLeast) return wa.isVersionAtLeast(v);
  const a = wa.version.split('.').map(Number);
  const b = v.split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return true;
}

/** Минимальная поддерживаемая версия Bot API (ux/12 §5.4): ниже — SCR-UNSUPPORTED. */
export const MIN_BOT_API = '7.0';

export function isPhone(wa: Pick<TgWebApp, 'platform'> | null): boolean {
  return wa ? ['ios', 'android', 'android_x'].includes(wa.platform) : false;
}
